import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import DailyIframe from "@daily-co/daily-js";
import { Mic, MicOff, Video as VideoIcon, VideoOff, MessageCircle, PhoneOff, X, Send, Users, Hand } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Avatar } from "@/components/brand";
import { cn } from "@/lib/utils";

const ROOM_IMG = "https://images.unsplash.com/photo-1586401426077-df67f1316076?w=1200&q=80";

function useCountdown(start, durationMin) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  if (!start || !durationMin) return { remaining: "--:--", started: false, elapsedMin: 0 };
  const end = new Date(start).getTime() + durationMin * 60000;
  const diff = Math.max(0, Math.floor((end - now) / 1000));
  const elapsed = Math.max(0, (now - new Date(start).getTime()) / 60000);
  return { remaining: `${String(Math.floor(diff / 60)).padStart(2, "0")}:${String(diff % 60).padStart(2, "0")}`, started: now >= new Date(start).getTime(), elapsedMin: elapsed };
}

export default function Live() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const frameRef = useRef(null);
  const containerRef = useRef(null);
  const [joined, setJoined] = useState(false);
  const [mic, setMic] = useState(false);
  const [cam, setCam] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [panel, setPanel] = useState("chat");
  const [text, setText] = useState("");
  const chatEnd = useRef(null);

  const room = useQuery({ queryKey: ["live", id], queryFn: () => api(`/live/${id}`), refetchInterval: 4000 });
  const roomId = room.data?.room_id;
  const isTrainer = !!room.data?.is_trainer;
  const controls = room.data?.controls ?? {};
  const ended = !!controls.ended;
  const { remaining, started } = useCountdown(room.data?.start_time, room.data?.duration_min);

  const messages = useQuery({ queryKey: ["live-messages", roomId], queryFn: () => api(`/live/${roomId}/messages`), enabled: !!roomId, refetchInterval: 3000 });
  const send = useMutation({ mutationFn: (t) => api(`/live/${roomId}/messages`, { method: "POST", body: { text: t } }), onSuccess: () => { setText(""); qc.invalidateQueries({ queryKey: ["live-messages", roomId] }); } });
  const control = useMutation({ mutationFn: (body) => api(`/live/${roomId}/controls`, { method: "POST", body }), onSuccess: () => qc.invalidateQueries({ queryKey: ["live", id] }) });

  const videoToken = useQuery({ queryKey: ["live-token", id], queryFn: () => api(`/live/${id}/video-token`, { method: "POST" }), enabled: !!room.data && !ended, retry: false });
  const videoInfo = videoToken.data;

  // Join Daily prebuilt frame when a token is available.
  useEffect(() => {
    if (!videoInfo?.enabled || !videoInfo.url || !containerRef.current || frameRef.current) return;
    const frame = DailyIframe.createFrame(containerRef.current, {
      showLeaveButton: false, showFullscreenButton: true,
      iframeStyle: { position: "absolute", inset: 0, width: "100%", height: "100%", border: "0" },
    });
    frameRef.current = frame;
    frame.on("joined-meeting", () => setJoined(true));
    frame.on("left-meeting", () => setJoined(false));
    frame.join({ url: videoInfo.url, token: videoInfo.token }).catch(() => {});
    return () => { try { frame.destroy(); } catch (e) {} frameRef.current = null; };
  }, [videoInfo]);

  useEffect(() => { if (chatOpen) setTimeout(() => chatEnd.current?.scrollIntoView(), 50); }, [messages.data?.length, chatOpen]);

  const toggleMic = () => { const n = !mic; setMic(n); frameRef.current?.setLocalAudio(n); };
  const toggleCam = () => { const n = !cam; setCam(n); frameRef.current?.setLocalVideo(n); };

  const leave = async () => {
    try { await frameRef.current?.leave(); } catch (e) {}
    if (!isTrainer) {
      if (started) { try { await api(`/bookings/${id}/complete`, { method: "POST" }); } catch (e) {} qc.invalidateQueries({ queryKey: ["bookings"] }); navigate(`/complete/${id}`); return; }
      navigate("/bookings"); return;
    }
    await control.mutateAsync({ ended: true });
    navigate("/trainer");
  };

  // Member auto-exit when trainer ends the class.
  useEffect(() => {
    if (ended && !isTrainer && room.data) { api(`/bookings/${id}/complete`, { method: "POST" }).catch(() => {}); navigate(`/complete/${id}`); }
  }, [ended, isTrainer, id, room.data, navigate]);

  const participants = room.data?.participants ?? [];
  const videoUnavailable = videoInfo && videoInfo.enabled === false;

  return (
    <div className="fixed inset-0 bg-live-bg text-white flex flex-col" data-testid="live-room-screen">
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-3">
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg truncate">{room.data?.title ?? "Connecting…"}</div>
          <div className="font-body text-xs text-live-muted truncate">with {room.data?.trainer?.name}</div>
        </div>
        <div className="flex items-center gap-1.5 h-7 px-3 rounded-pill bg-brand" data-testid="live-badge">
          <span className="w-1.5 h-1.5 rounded-full bg-white animate-live-pulse" /><span className="font-display text-xs tracking-widest">LIVE</span>
        </div>
        <div className="text-right"><div className="font-display text-lg tabular-nums" data-testid="live-timer">{remaining}</div><div className="font-body text-[10px] text-live-muted">remaining</div></div>
      </div>

      {/* Stage */}
      <div className="flex-1 px-5 min-h-0">
        <div className="relative w-full h-full rounded-xl2 overflow-hidden bg-live-surface" data-testid="live-stage">
          <div ref={containerRef} className="absolute inset-0" />
          {!joined ? (
            <div className="absolute inset-0">
              <img src={room.data?.trainer?.avatar || ROOM_IMG} alt="" className="w-full h-full object-cover opacity-60" />
              <div className="absolute inset-0 bg-gradient-to-t from-live-bg via-transparent to-transparent" />
              <div className="font-accent text-2xl text-brand absolute top-4 left-4 -rotate-6 leading-tight">Stronger<br />Healthier<br />Happier<br />Together</div>
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-6">
                {videoUnavailable ? (
                  <>
                    <VideoIcon size={30} className="text-live-muted" />
                    <div className="font-display text-lg">Live video not configured</div>
                    <div className="font-body text-sm text-live-muted max-w-sm">{videoInfo.reason || "The video service needs a Daily.co API key."} You can still use class chat below.</div>
                  </>
                ) : videoToken.isFetching ? (
                  <div className="font-body text-sm text-live-muted">Connecting to video…</div>
                ) : (
                  <div className="font-body text-sm text-live-muted">Preparing your live room…</div>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Trainer controls */}
      {isTrainer ? (
        <div className="flex gap-2 px-5 pt-2 overflow-x-auto no-scrollbar">
          <CtrlPill testID="ctrl-mute-all" active={controls.mute_all} icon={MicOff} label={controls.mute_all ? "Unmute all" : "Mute all"} onClick={() => control.mutate({ mute_all: !controls.mute_all })} />
          <CtrlPill testID="ctrl-allow-mics" active={controls.allow_mics === false} icon={Mic} label={controls.allow_mics === false ? "Mics off" : "Mics on"} onClick={() => control.mutate({ allow_mics: !(controls.allow_mics !== false) })} />
          <CtrlPill testID="ctrl-allow-video" active={controls.allow_video === false} icon={VideoIcon} label={controls.allow_video === false ? "Video off" : "Video on"} onClick={() => control.mutate({ allow_video: !(controls.allow_video !== false) })} />
        </div>
      ) : null}

      {/* Bottom bar */}
      <div className="flex items-start justify-around px-4 pt-3 pb-5">
        <Ctrl icon={mic ? Mic : MicOff} label={mic ? "Mute" : "Unmute"} testID="ctrl-mic" onClick={toggleMic} active={!mic} disabled={!joined} />
        <Ctrl icon={cam ? VideoIcon : VideoOff} label={cam ? "Camera" : "Cam off"} testID="ctrl-camera" onClick={toggleCam} active={!cam} disabled={!joined} />
        <Ctrl icon={MessageCircle} label="Chat" testID="ctrl-chat" onClick={() => { setPanel("chat"); setChatOpen(true); }} badge={messages.data?.length} />
        {isTrainer
          ? <Ctrl icon={Users} label="People" testID="ctrl-participants" onClick={() => { setPanel("participants"); setChatOpen(true); }} />
          : <Ctrl icon={Hand} label="Raise" testID="ctrl-hand" onClick={() => send.mutate("🙋 raised their hand")} />}
        <button data-testid="ctrl-leave" onClick={leave} className={cn("flex flex-col items-center justify-center w-16 h-[54px] rounded-pill gap-0.5", isTrainer ? "border-[1.5px] border-brand text-brand" : "bg-brand text-white")}>
          {isTrainer ? <X size={20} /> : <PhoneOff size={20} />}
          <span className="font-body font-semibold text-[10px]">{isTrainer ? "End" : "Leave"}</span>
        </button>
      </div>

      {/* Chat / participants panel */}
      {chatOpen ? (
        <div className="absolute inset-x-0 bottom-0 top-[35%] bg-live-surface rounded-t-xl2 border border-live-border flex flex-col">
          <div className="flex items-center gap-2 p-3 border-b border-live-border">
            <button data-testid="panel-tab-chat" onClick={() => setPanel("chat")} className={cn("flex items-center gap-1.5 px-3 h-8 rounded-pill font-body font-semibold text-sm", panel === "chat" ? "bg-white/10 text-brand" : "text-live-muted")}><MessageCircle size={14} />Chat</button>
            <button data-testid="panel-tab-participants" onClick={() => setPanel("participants")} className={cn("flex items-center gap-1.5 px-3 h-8 rounded-pill font-body font-semibold text-sm", panel === "participants" ? "bg-white/10 text-brand" : "text-live-muted")}><Users size={14} />Participants ({participants.length})</button>
            <div className="flex-1" />
            <button data-testid="panel-close" onClick={() => setChatOpen(false)} className="text-white p-1"><X size={20} /></button>
          </div>
          {panel === "chat" ? (
            <>
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {(messages.data ?? []).length === 0 ? <div className="text-center text-live-muted font-body text-sm py-6">Say hi to the class 👋</div> : (messages.data ?? []).map((m) => (
                  <div key={m.id} className="bg-white/5 p-3 rounded-md">
                    <div className={cn("font-body font-semibold text-xs", m.role === "trainer" ? "text-brand" : "text-live-muted")}>{m.user_name}{m.role === "trainer" ? " (Trainer)" : ""}</div>
                    <div className="font-body text-sm text-white">{m.text}</div>
                  </div>
                ))}
                <div ref={chatEnd} />
              </div>
              <form onSubmit={(e) => { e.preventDefault(); if (text.trim()) send.mutate(text.trim()); }} className="flex items-center gap-2 p-3 border-t border-live-border">
                <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" className="flex-1 h-11 px-4 rounded-pill bg-live-bg border border-live-border text-white font-body text-sm outline-none" />
                <button type="submit" data-testid="chat-send" className="w-11 h-11 rounded-full bg-brand flex items-center justify-center"><Send size={18} /></button>
              </form>
            </>
          ) : (
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              <div className="flex items-center gap-3 p-2"><Avatar size={36} name={room.data?.trainer?.name} uri={room.data?.trainer?.avatar} /><span className="font-body font-semibold text-sm">{room.data?.trainer?.name} (Trainer)</span></div>
              {participants.map((p) => <div key={p.id} className="flex items-center gap-3 p-2"><Avatar size={36} name={p.name} /><span className="font-body font-semibold text-sm">{p.name}</span></div>)}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

function Ctrl({ icon: Icon, label, onClick, testID, active, disabled, badge }) {
  return (
    <button data-testid={testID} onClick={onClick} disabled={disabled} className="flex flex-col items-center gap-1.5 w-16 disabled:opacity-40">
      <div className={cn("relative w-[54px] h-[54px] rounded-full flex items-center justify-center border border-live-border", active ? "bg-brand-deep border-brand-deep" : "bg-white/10")}>
        <Icon size={20} className="text-white" />
        {badge ? <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-brand text-white text-[10px] font-bold flex items-center justify-center">{badge > 99 ? "99+" : badge}</span> : null}
      </div>
      <span className="font-body text-[11px] text-live-muted">{label}</span>
    </button>
  );
}

function CtrlPill({ icon: Icon, label, onClick, testID, active }) {
  return (
    <button data-testid={testID} onClick={onClick} className={cn("flex items-center gap-1.5 h-8 px-3 rounded-pill border shrink-0 font-body font-semibold text-xs text-white", active ? "bg-brand-deep border-brand-deep" : "bg-live-surface border-live-border")}>
      <Icon size={14} />{label}
    </button>
  );
}
